/**
 * Hook for fetching user information in batch
 */

import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '@/lib/api/client';

export interface UserDisplayInfo {
  user_id: string;
  username: string;
  nickname: string;
  display_name: string;
  birth_chart_name: string | null;
  email: string | null;
  phone_number: string | null;
  avatar_url: string | null;
  role_level: string;
  is_admin: boolean;
  is_beta_tester: boolean;
  system_role: string;
}

export interface UserBasicInfo {
  user_id: string;
  username: string;
  nickname: string;
  avatar_url: string | null;
}

type UserInfo = UserDisplayInfo | UserBasicInfo;

export const useUsersBatch = (userIds: string[], infoLevel: 'basic' | 'display' = 'display') => {
  const [users, setUsers] = useState<Record<string, UserInfo>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userIdsKey = useMemo(() => JSON.stringify(userIds), [userIds]);

  useEffect(() => {
    if (!userIds || userIds.length === 0) {
      setUsers({});
      return;
    }

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.post<Record<string, UserInfo>>('/users/batch', {
          user_ids: userIds,
          info_level: infoLevel,
        });

        setUsers(response.data);
      } catch (err) {
        console.error('Failed to fetch users batch:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [userIdsKey, infoLevel, userIds]);

  return { users, loading, error };
};
